import React, { useState, useRef, useEffect } from "react";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import article from "./InvestoContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
);

const WealthProjectionCalculator = () => {
  const content = article.find((item) => item.id === 7);
  const [activeTab, setActiveTab] = useState("projection");
  const [isMobile, setIsMobile] = useState(false);
  const [savedProjections, setSavedProjections] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [riskProfile, setRiskProfile] = useState("moderate");
  const [notifications, setNotifications] = useState([]);
  const [calculationHistory, setCalculationHistory] = useState([]);

  const [inputs, setInputs] = useState({
    // Basic inputs
    initialInvestment: "10000",
    monthlyContribution: "500",
    annualReturn: "8",
    timePeriod: "20",
    inflationRate: "2.5",
    
    // Advanced inputs
    contributionIncrease: "3",
    marketVolatility: "15",
    taxRate: "15",
    fees: "0.25",
    
    // Scenario inputs
    lumpSumAmount: "0",
    lumpSumYear: "5",
    withdrawalRate: "4",
    retirementAge: "65",
    currentAge: "35"
  });

  const [projection, setProjection] = useState(null);
  const chartRef = useRef();

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "projection") {
      calculateWealthProjection();
    }
  }, [inputs, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const calculateWealthProjection = () => {
    const {
      initialInvestment,
      monthlyContribution,
      annualReturn,
      timePeriod,
      inflationRate,
      contributionIncrease,
      marketVolatility,
      taxRate,
      fees,
      lumpSumAmount,
      lumpSumYear,
      withdrawalRate
    } = inputs;

    if (!initialInvestment || !annualReturn || !timePeriod) {
      return;
    }

    const initial = parseFloat(initialInvestment);
    const monthly = parseFloat(monthlyContribution || 0);
    const years = parseInt(timePeriod);
    const annualRate = parseFloat(annualReturn) / 100;
    const inflation = parseFloat(inflationRate) / 100;
    const contributionGrowth = parseFloat(contributionIncrease) / 100;
    const volatility = parseFloat(marketVolatility) / 100;
    const tax = parseFloat(taxRate) / 100;
    const annualFees = parseFloat(fees) / 100;
    const lumpSum = parseFloat(lumpSumAmount || 0);
    const lumpSumAtYear = parseInt(lumpSumYear || 0);

    const projectionData = [];
    let totalValue = initial;
    let totalContributions = initial;
    let totalGrowth = 0;
    let currentMonthlyContribution = monthly;

    // Calculate nominal projection (without inflation)
    for (let year = 0; year <= years; year++) {
      if (year > 0) {
        // Apply market return with some randomness for volatility visualization
        const effectiveReturn = annualRate - annualFees;
        totalValue = totalValue * (1 + effectiveReturn);
        
        // Add monthly contributions for the year
        for (let month = 0; month < 12; month++) {
          totalValue += currentMonthlyContribution;
          totalContributions += currentMonthlyContribution;
        }
        
        // Increase contributions for next year (if applicable)
        currentMonthlyContribution *= (1 + contributionGrowth);
        
        // Add lump sum if applicable
        if (year === lumpSumAtYear) {
          totalValue += lumpSum;
          totalContributions += lumpSum;
        }
      }

      // Calculate real value (adjusted for inflation)
      const realValue = totalValue / Math.pow(1 + inflation, year);
      const realContributions = totalContributions / Math.pow(1 + inflation, year);

      projectionData.push({
        year,
        nominalValue: totalValue,
        realValue: realValue,
        contributions: totalContributions,
        realContributions: realContributions,
        growth: totalValue - totalContributions,
        monthlyIncome: totalValue * (parseFloat(withdrawalRate) / 100 / 12)
      });
    }

    const finalNominal = projectionData[projectionData.length - 1].nominalValue;
    const finalReal = projectionData[projectionData.length - 1].realValue;
    const totalContributed = projectionData[projectionData.length - 1].contributions;
    const totalEarned = finalNominal - totalContributed;

    const projectionResult = {
      projectionData,
      summary: {
        finalNominalValue: finalNominal,
        finalRealValue: finalReal,
        totalContributions: totalContributed,
        totalEarnings: totalEarned,
        earningsPercentage: (totalEarned / totalContributed) * 100,
        monthlyRetirementIncome: finalNominal * (parseFloat(withdrawalRate) / 100 / 12),
        yearsToDouble: 72 / parseFloat(annualReturn),
        requiredSavings: (parseFloat(inputs.monthlyContribution) * 12 * years) / finalNominal * 100
      },
      metrics: {
        annualReturn: parseFloat(annualReturn),
        inflationAdjustedReturn: ((1 + annualRate) / (1 + inflation) - 1) * 100,
        safeWithdrawalAmount: finalNominal * 0.04,
        fiAge: calculateFinancialIndependenceAge(projectionData, parseFloat(inputs.currentAge))
      }
    };

    setProjection(projectionResult);
  };

  const calculateFinancialIndependenceAge = (data, currentAge) => {
    const monthlyExpenses = parseFloat(inputs.monthlyContribution) * 2; // Estimate
    const target = monthlyExpenses * 12 * 25; // 25x annual expenses (4% rule)
    
    for (let i = 0; i < data.length; i++) {
      if (data[i].nominalValue >= target) {
        return currentAge + i;
      }
    }
    return null;
  };

  const calculateRetirementReadiness = () => {
    if (!projection) return null;

    const { finalNominalValue, monthlyRetirementIncome } = projection.summary;
    const targetIncome = parseFloat(inputs.monthlyContribution) * 3; // 3x current savings rate as target
    const readiness = (monthlyRetirementIncome / targetIncome) * 100;

    return {
      score: Math.min(100, readiness),
      status: readiness >= 100 ? "On Track" : readiness >= 75 ? "Moderate" : "Needs Attention",
      color: readiness >= 100 ? "#28A745" : readiness >= 75 ? "#FFC107" : "#E53E3E"
    };
  };

  const resetCalculator = () => {
    setInputs({
      initialInvestment: "10000",
      monthlyContribution: "500",
      annualReturn: "8",
      timePeriod: "20",
      inflationRate: "2.5",
      contributionIncrease: "3",
      marketVolatility: "15",
      taxRate: "15",
      fees: "0.25",
      lumpSumAmount: "0",
      lumpSumYear: "5",
      withdrawalRate: "4",
      retirementAge: "65",
      currentAge: "35"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!projection) return;
    
    const headers = "Year,Nominal Value,Real Value (Inflation Adjusted),Total Contributions,Investment Growth,Monthly Retirement Income\n";
    const rows = projection.projectionData.map(data => 
      `${data.year},$${data.nominalValue.toFixed(2)},$${data.realValue.toFixed(2)},$${data.contributions.toFixed(2)},$${data.growth.toFixed(2)},$${data.monthlyIncome.toFixed(2)}`
    ).join('\n');
    
    const csvContent = headers + rows;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wealth-projection.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Projection exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!projection) return;
    
    const newProjection = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      summary: projection.summary
    };
    
    setCalculationHistory(prev => [newProjection, ...prev.slice(0, 9)]);
    addNotification("Projection saved to history", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { annualReturn: "6", marketVolatility: "10", inflationRate: "2.5" },
      moderate: { annualReturn: "8", marketVolatility: "15", inflationRate: "2.5" },
      aggressive: { annualReturn: "10", marketVolatility: "20", inflationRate: "2.5" }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} risk profile applied`, "info");
  };

  // Chart Data
  const projectionChartData = projection ? {
    labels: projection.projectionData.map(d => `Year ${d.year}`),
    datasets: [
      {
        label: 'Nominal Portfolio Value',
        data: projection.projectionData.map(d => d.nominalValue),
        borderColor: "#19692C",
        backgroundColor: "rgba(25, 105, 44, 0.1)",
        fill: true,
        tension: 0.4,
        pointBackgroundColor: "#19692C",
        pointBorderColor: "#FFFFFF",
        pointBorderWidth: 2
      },
      {
        label: 'Inflation Adjusted Value',
        data: projection.projectionData.map(d => d.realValue),
        borderColor: "#FFD93D",
        backgroundColor: "rgba(255, 217, 61, 0.1)",
        fill: false,
        borderDash: [5, 5],
        tension: 0.4
      },
      {
        label: 'Total Contributions',
        data: projection.projectionData.map(d => d.contributions),
        borderColor: "#4A5568",
        backgroundColor: "rgba(74, 85, 104, 0.1)",
        fill: false,
        borderDash: [3, 3],
        tension: 0.4
      }
    ]
  } : null;

  const allocationChartData = {
    labels: ['Stocks', 'Bonds', 'Real Estate', 'Cash'],
    datasets: [{
      data: [60, 30, 7, 3],
      backgroundColor: ["#19692C", "#2B8C3A", "#28A745", "#E8F5EB"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  };

  const retirementReadiness = calculateRetirementReadiness();

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Wealth Projection Calculator</h1>
        </div>
        <p>Plan your financial future with detailed investment projections and retirement planning</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "projection" ? "active" : ""}`}
                onClick={() => setActiveTab("projection")}
              >
                <i className="fas fa-chart-line"></i>
                {isMobile ? 'Projection' : 'Wealth Projection'}
              </button>
              <button 
                className={`tab ${activeTab === "retirement" ? "active" : ""}`}
                onClick={() => setActiveTab("retirement")}
              >
                <i className="fas fa-piggy-bank"></i>
                {isMobile ? 'Retirement' : 'Retirement Planning'}
              </button>
              <button 
                className={`tab ${activeTab === "scenarios" ? "active" : ""}`}
                onClick={() => setActiveTab("scenarios")}
              >
                <i className="fas fa-chart-bar"></i>
                {isMobile ? 'Scenarios' : 'Scenario Analysis'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Risk Profile Presets */}
          <div className="presets-section">
            <h4>Risk Profile Presets:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Projection Tab */}
          {activeTab === "projection" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-seedling"></i>
                    Investment Basics
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Initial Investment</label>
                      <input 
                        type="number" 
                        name="initialInvestment" 
                        value={inputs.initialInvestment} 
                        onChange={handleChange}
                        placeholder="10000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar-plus"></i> Monthly Contribution</label>
                      <input 
                        type="number" 
                        name="monthlyContribution" 
                        value={inputs.monthlyContribution} 
                        onChange={handleChange}
                        placeholder="500"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-line"></i> Expected Annual Return (%)</label>
                      <input 
                        type="number" 
                        name="annualReturn" 
                        value={inputs.annualReturn} 
                        onChange={handleChange}
                        placeholder="8"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-clock"></i> Time Period (Years)</label>
                      <input 
                        type="number" 
                        name="timePeriod" 
                        value={inputs.timePeriod} 
                        onChange={handleChange}
                        placeholder="20"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Advanced Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-line"></i> Inflation Rate (%)</label>
                      <input 
                        type="number" 
                        name="inflationRate" 
                        value={inputs.inflationRate} 
                        onChange={handleChange}
                        placeholder="2.5"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-up"></i> Contribution Increase (%)</label>
                      <input 
                        type="number" 
                        name="contributionIncrease" 
                        value={inputs.contributionIncrease} 
                        onChange={handleChange}
                        placeholder="3"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-wave-square"></i> Market Volatility (%)</label>
                      <input 
                        type="number" 
                        name="marketVolatility" 
                        value={inputs.marketVolatility} 
                        onChange={handleChange}
                        placeholder="15"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Annual Fees (%)</label>
                      <input 
                        type="number" 
                        name="fees" 
                        value={inputs.fees} 
                        onChange={handleChange}
                        placeholder="0.25"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Retirement Planning Tab */}
          {activeTab === "retirement" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-piggy-bank"></i>
                  Retirement Planning
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-user"></i> Current Age</label>
                    <input 
                      type="number" 
                      name="currentAge" 
                      value={inputs.currentAge} 
                      onChange={handleChange}
                      placeholder="35"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-flag"></i> Retirement Age</label>
                    <input 
                      type="number" 
                      name="retirementAge" 
                      value={inputs.retirementAge} 
                      onChange={handleChange}
                      placeholder="65"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-hand-holding-usd"></i> Withdrawal Rate (%)</label>
                    <input 
                      type="number" 
                      name="withdrawalRate" 
                      value={inputs.withdrawalRate} 
                      onChange={handleChange}
                      placeholder="4"
                      step="0.1"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-file-invoice-dollar"></i> Tax Rate (%)</label>
                    <input 
                      type="number" 
                      name="taxRate" 
                      value={inputs.taxRate} 
                      onChange={handleChange}
                      placeholder="15"
                      step="0.1"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Scenario Analysis Tab */}
          {activeTab === "scenarios" && (
            <div className="risk-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-chart-bar"></i>
                  Scenario Analysis
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-money-bill-wave"></i> Future Lump Sum</label>
                    <input 
                      type="number" 
                      name="lumpSumAmount" 
                      value={inputs.lumpSumAmount} 
                      onChange={handleChange}
                      placeholder="0"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-calendar-star"></i> Lump Sum Year</label>
                    <input 
                      type="number" 
                      name="lumpSumYear" 
                      value={inputs.lumpSumYear} 
                      onChange={handleChange}
                      placeholder="5"
                    />
                  </div><br/>
                </div>
                
                <div className="scenario-list">
                  <div className="scenario-item">
                    <div className="scenario-name">Early Retirement Scenario</div>
                    <div className="scenario-outcome">+15 years of freedom</div>
                  </div>
                  <div className="scenario-item">
                    <div className="scenario-name">Market Downturn Scenario</div>
                    <div className="scenario-outcome">-20% portfolio impact</div>
                  </div>
                  <div className="scenario-item">
                    <div className="scenario-name">Increased Savings Scenario</div>
                    <div className="scenario-outcome">+25% faster growth</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Projection History
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No projections yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className="history-profit positive">
                          <i className="fas fa-arrow-up"></i>
                          ${calc.summary.finalNominalValue.toFixed(0)}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-chart-line"></i> Return: {calc.summary.earningsPercentage.toFixed(1)}%</span>
                        <span><i className="fas fa-hand-holding-usd"></i> Monthly: ${calc.summary.monthlyRetirementIncome.toFixed(0)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "projection" || activeTab === "retirement" || activeTab === "scenarios") && (
          <div className="right-panel">
            {activeTab === "projection" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-chart-line"></i>
                    Wealth Projection
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {projection ? (
                  <>
                    {/* Key Metrics */}
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value positive">
                          ${projection.summary.finalNominalValue.toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-money-bill-wave"></i>
                          Final Portfolio Value
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${projection.summary.finalRealValue.toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-chart-line"></i>
                          Inflation Adjusted
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {projection.summary.earningsPercentage.toFixed(1)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          Return on Contributions
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {projection.summary.yearsToDouble.toFixed(1)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-hourglass-half"></i>
                          Years to Double
                        </div>
                      </div>
                    </div>

                    {/* Detailed Results */}
                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-piggy-bank"></i> Total Contributions:</span>
                        <span>${projection.summary.totalContributions.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-coins"></i> Investment Earnings:</span>
                        <span>${projection.summary.totalEarnings.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-hand-holding-usd"></i> Monthly Retirement Income:</span>
                        <span>${projection.summary.monthlyRetirementIncome.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-shield-alt"></i> Safe Withdrawal (4% Rule):</span>
                        <span>${projection.metrics.safeWithdrawalAmount.toLocaleString(undefined, {maximumFractionDigits: 0})}/year</span>
                      </div>
                    </div>

                    {/* Projection Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-project-diagram"></i>
                        Wealth Growth Projection
                      </h3>
                      <Line 
                        data={projectionChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: true,
                              position: 'top'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `${context.dataset.label}: $${context.parsed.y.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
                                }
                              }
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                callback: function(value) {
                                  return '$' + value.toLocaleString(undefined, {maximumFractionDigits: 0});
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Asset Allocation */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Recommended Asset Allocation
                      </h3>
                      <Doughnut 
                        data={allocationChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'right'
                            }
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-calculator fa-3x"></i>
                    <p>Enter your investment details to see projections</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "retirement" && projection && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-piggy-bank"></i>
                  Retirement Readiness
                </h3>
                
                {/* Retirement Readiness Meter */}
                <div className="risk-summary">
                  <h4>Retirement Readiness Score</h4>
                  <div className="risk-meter">
                    <div className="meter-label">Readiness Score: {retirementReadiness.score.toFixed(0)}%</div>
                    <div className="meter-bar">
                      <div 
                        className="meter-fill"
                        style={{ 
                          width: `${retirementReadiness.score}%`,
                          backgroundColor: retirementReadiness.color
                        }}
                      ></div>
                    </div>
                    <div className="meter-value" style={{ color: retirementReadiness.color }}>
                      {retirementReadiness.status}
                    </div>
                  </div>
                </div>

                {/* Retirement Metrics */}
                <div className="projection-summary">
                  <div className="summary-card">
                    <div className="summary-value">
                      {projection.metrics.fiAge || "N/A"}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-bullseye"></i>
                      Financial Independence Age
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      ${projection.summary.monthlyRetirementIncome.toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-hand-holding-usd"></i>
                      Monthly Income
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {inputs.retirementAge}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-flag"></i>
                      Target Age
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {parseInt(inputs.retirementAge) - parseInt(inputs.currentAge)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-clock"></i>
                      Years Left
                    </div>
                  </div>
                </div>

                {/* Retirement Timeline */}
                <div className="chart-container">
                  <h3>
                    <i className="fas fa-road"></i>
                    Retirement Timeline
                  </h3>
                  <Bar 
                    data={{
                      labels: ['Current', '5 Years', '10 Years', 'Retirement'],
                      datasets: [{
                        label: 'Portfolio Value',
                        data: [
                          projection.projectionData[0].nominalValue,
                          projection.projectionData[5]?.nominalValue || 0,
                          projection.projectionData[10]?.nominalValue || 0,
                          projection.summary.finalNominalValue
                        ],
                        backgroundColor: [
                          'rgba(25, 105, 44, 0.6)',
                          'rgba(25, 105, 44, 0.7)',
                          'rgba(25, 105, 44, 0.8)',
                          'rgba(25, 105, 44, 1)'
                        ],
                        borderColor: '#19692C',
                        borderWidth: 2
                      }]
                    }}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: {
                          display: false
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              return `Portfolio: $${context.parsed.y.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
                            }
                          }
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback: function(value) {
                              return '$' + value.toLocaleString(undefined, {maximumFractionDigits: 0});
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
              </div>
            )}

            {activeTab === "scenarios" && projection && (
              <div className="risk-results-panel">
                <h3>
                  <i className="fas fa-chart-bar"></i>
                  Scenario Comparison
                </h3>
                
                <div className="scenario-comparison">
                  <div className="scenario-item">
                    <div className="scenario-name">Base Case</div>
                    <div className="scenario-stats">
                      <div className="breakdown-item">
                        <span>Final Value:</span>
                        <span>${projection.summary.finalNominalValue.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Monthly Income:</span>
                        <span>${projection.summary.monthlyRetirementIncome.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="scenario-item">
                    <div className="scenario-name">Optimistic (+2% Return)</div>
                    <div className="scenario-stats">
                      <div className="breakdown-item">
                        <span>Final Value:</span>
                        <span>${(projection.summary.finalNominalValue * 1.3).toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Monthly Income:</span>
                        <span>${(projection.summary.monthlyRetirementIncome * 1.3).toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="scenario-item">
                    <div className="scenario-name">Pessimistic (-2% Return)</div>
                    <div className="scenario-stats">
                      <div className="breakdown-item">
                        <span>Final Value:</span>
                        <span>${(projection.summary.finalNominalValue * 0.7).toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Monthly Income:</span>
                        <span>${(projection.summary.monthlyRetirementIncome * 0.7).toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default WealthProjectionCalculator;